import { EMPTY_ARRAY } from "client/Misc/utils";
import { never } from "client_legacy/ndaw/errors";
import {
	Fragment,
	ReactElement,
	ReactNode
} from "react";

type ReplacementWhatVersion = {
	what: string;
};

type ReplacementRangeVersion = {
	from: number;
	to: number;
};

export type FormattedTextReplacement = {
	filler: ReactNode;
} & (ReplacementWhatVersion | ReplacementRangeVersion);

export type FormattedTextWrappers = {
	blockId: string;
	wrap: (children: ReactNode) => ReactElement;
};

export type FormattedTextProps = {
	text: string;
} & (
	{
		thingsToReplace: readonly FormattedTextReplacement[];
		thingsToWrap?: readonly FormattedTextWrappers[];
	}
	|
	{
		thingsToReplace?: readonly FormattedTextReplacement[];
		thingsToWrap: readonly FormattedTextWrappers[];
	}
);

export const FormattedText = (props: FormattedTextProps): ReactElement => {
	const {
		text,
		thingsToReplace = EMPTY_ARRAY,
		thingsToWrap = EMPTY_ARRAY
	} = props;

	const tree: BlockNode = buildTree(
		text,
		thingsToReplace,
		thingsToWrap
	);

	const renderNode = (node: Node): ReactNode => {
		switch (node.kind) {
			case "text":
				return node.content;
			case "wrap":
				return node.wrap(
					renderNode(node.content)
				);
			case "replacement":
				return node.content;
			case "block":
				const { content } = node;
				return (
					<>
						{content.map((x, i) => (
							<Fragment key={i}>
								{renderNode(x)}
							</Fragment>
						))}
					</>
				);
			default:
				never(node);
		}
	};

	return (
		<>
			{renderNode(tree)}
		</>
	);
};
FormattedText.displayName = "FormattedText";

type IdentifiedThing = IdentifiedReplacement | IdentifiedWrapping;

type IdentifiedReplacement = {
	kind: "replacement";
	start: number;
	end: number;
	filler: ReactNode;
};

type IdentifiedWrapping = {
	kind: "wrapping";
	start: number;
	end: number;
	contentStart: number;
	contentEnd: number;
	wrap: (children: ReactNode) => ReactElement;
};

function identifyThings(
	text: string,
	thingsToReplace: readonly FormattedTextReplacement[],
	thingsToWrap: readonly FormattedTextWrappers[]
): IdentifiedThing[] {
	const result: IdentifiedThing[] = [];

	for (const thingToReplace of thingsToReplace) {
		if (isWhatVersion(thingToReplace)) {
			const { what, filler } = thingToReplace;

			let index: number = -1;

			while (true) {
				index = text.indexOf(what, index + 1);

				if (index === -1) {
					break;
				}

				result.push({
					kind: "replacement",
					start: index,
					end: index + what.length,
					filler
				});
			}
		} else {
			const {
				from,
				to,
				filler
			} = thingToReplace;

			if (from >= to
				|| from > text.length
				|| to > text.length
			) {
				console.warn(`The provided range '${from}' - '${to}' is not valid ('${text.length}')`);
				continue;
			}

			result.push({
				kind: "replacement",
				start: from,
				end: to,
				filler
			});
		}
	}

	const regex: RegExp = /(?:\r\n|\r|\n)/g;
	let match: RegExpExecArray | null = regex.exec(text);
	while (match !== null) {
		if (match.index === regex.lastIndex) {
			regex.lastIndex++;
		}

		result.push({
			kind: "replacement",
			start: match.index,
			end: match.index + match[0].length,
			filler: <br />
		});

		match = regex.exec(text);
	}

	for (const thingToWrap of thingsToWrap) {
		const { blockId, wrap } = thingToWrap;

		const startTag: string = `[[${blockId}]]`;
		const start: number = text.indexOf(startTag);
		if (start === -1) {
			continue;
		}

		const endTag: string = `[[/${blockId}]]`;
		const endIndex: number = text.indexOf(endTag, start + startTag.length);

		if (endIndex === -1) {
			console.warn(`No end tag found for '${blockId}'`);
			continue;
		}

		let contentStart: number = start + startTag.length;
		while (text[contentStart] === " " && contentStart < endIndex) {
			contentStart += 1;
		}

		let contentEnd: number = endIndex;
		while (text[contentEnd - 1] === " " && contentEnd > contentStart) {
			contentEnd -= 1;
		}

		result.push({
			kind: "wrapping",
			start,
			end: endIndex + endTag.length,
			contentStart,
			contentEnd,
			wrap
		});
	}

	return result;
}

function isWhatVersion(value: ReplacementWhatVersion | ReplacementRangeVersion): value is ReplacementWhatVersion {
	return typeof (value as ReplacementWhatVersion).what === "string";
}

function buildTree(
	text: string,
	thingsToReplace: readonly FormattedTextReplacement[],
	thingsToWrap: readonly FormattedTextWrappers[]
): BlockNode {
	const identifiedThings: IdentifiedThing[] = identifyThings(
		text,
		thingsToReplace,
		thingsToWrap
	);

	identifiedThings.sort((a, b) => a.start - b.start);

	return buildBlock(
		text,
		0,
		text.length,
		identifiedThings
	);
}

function buildBlock(
	text: string,
	start: number,
	end: number,
	identifiedThings: readonly IdentifiedThing[]
): BlockNode {

	const content: Node[] = [];

	let lastTextIndex: number = start;

	for (let i = 0; i < identifiedThings.length; i++) {
		const thing: IdentifiedThing = identifiedThings[i];

		if (thing.start < lastTextIndex) {
			// Assumption is that some other node already processed it (since it is a flat list)
			continue;
		}

		if (thing.end > end) {
			console.warn("thing exceeds the block", thing);
			break;
		}

		if (lastTextIndex < thing.start) {
			const node: TextNode = buildText(
				text,
				lastTextIndex,
				thing.start
			);

			content.push(node);

			lastTextIndex = node.end;
		}

		switch (thing.kind) {
			case "replacement":
				{
					const node: ReplacementNode = buildReplacement(
						thing.start,
						thing.end,
						thing.filler
					);

					content.push(node);
					break;
				}
			case "wrapping":
				{
					const endIndex: number = identifiedThings.findIndex(
						(x) => x.start >= thing.end);

					const node: WrapNode = buildWrap(
						text,
						thing,
						identifiedThings.slice(
							i + 1,
							endIndex === -1 ? undefined : endIndex
						)
					);

					content.push(node);
					break;
				}
			default:
				never(thing);
		}

		lastTextIndex = content[content.length - 1].end;
	}

	if (lastTextIndex < end) {
		const node: TextNode = buildText(
			text,
			lastTextIndex,
			end
		);

		content.push(node);

		lastTextIndex = node.end;
	}

	return {
		kind: "block",
		start,
		end,
		content
	};
}

function buildText(
	text: string,
	start: number,
	end: number,
): TextNode {
	return {
		kind: "text",
		start,
		end,
		content: text.substring(start, end)
	};
}

function buildReplacement(
	start: number,
	end: number,
	filler: ReactNode
): ReplacementNode {
	return {
		kind: "replacement",
		start,
		end,
		content: filler
	};
}

function buildWrap(
	text: string,
	thing: IdentifiedWrapping,
	identifiedThings: readonly IdentifiedThing[]
): WrapNode {
	const {
		start,
		contentStart,
		end,
		contentEnd,
		wrap
	} = thing;

	return {
		kind: "wrap",
		start,
		end,
		wrap,
		content: buildBlock(
			text,
			contentStart,
			contentEnd,
			identifiedThings
		)
	};
}

type Node = BlockNode | TextNode | ReplacementNode | WrapNode;

type BlockNode = {
	kind: "block";
	start: number;
	end: number;
	content: Node[];
};

type WrapNode = {
	kind: "wrap";
	start: number;
	end: number;
	wrap: (children: ReactNode) => ReactElement;
	content: BlockNode;
};

type TextNode = {
	kind: "text";
	start: number;
	end: number;
	content: string;
};

type ReplacementNode = {
	kind: "replacement";
	start: number;
	end: number;
	content: ReactNode;
};
